import { computed, onScopeDispose, shallowRef, watch, type ComputedRef } from 'vue'

import type { WorkspacePreferenceScope } from '../api/WorkspacePreferencesApi'
import type { WorkspacePreferenceEntry } from '../models/workspacePreference.wire'
import {
  workspacePreferencesService,
  type WorkspacePreferenceListSnapshot,
  type WorkspacePreferenceSnapshot,
  type WorkspacePreferencesService,
} from '../service/WorkspacePreferencesService'

import { useAccount } from './useAccount'
import { useWorkspace } from './useWorkspace'

export interface UseWorkspacePreferences {
  entries: ComputedRef<readonly WorkspacePreferenceEntry[]>
  listState: ComputedRef<WorkspacePreferenceListSnapshot>
  preference: <T = unknown>(key: string) => ComputedRef<WorkspacePreferenceSnapshot<T>>
  list: (options?: { force?: boolean }) => Promise<readonly WorkspacePreferenceEntry[]>
  get: <T = unknown>(key: string, options?: { force?: boolean }) => Promise<T | undefined>
  upsert: <T>(key: string, value: T) => Promise<void>
}

/** Generic current-workspace preference seam; consumer-owned keys and DTOs stay outside module 07. */
export function useWorkspacePreferences(
  service: WorkspacePreferencesService = workspacePreferencesService,
): UseWorkspacePreferences {
  const account = useAccount()
  const workspace = useWorkspace()
  const revision = shallowRef(0)

  const scope = computed<WorkspacePreferenceScope | null>(() => {
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    return accountId === undefined || workspaceId === undefined ? null : { accountId, workspaceId }
  })
  const scopeIdentity = computed(() => {
    const current = scope.value
    return current ? `${current.accountId}:${String(current.workspaceId)}` : ''
  })

  const unsubscribe = service.subscribe(() => {
    revision.value += 1
  })
  onScopeDispose(unsubscribe)
  watch(scopeIdentity, () => service.activate(scope.value), { immediate: true, flush: 'sync' })

  const listState = computed(() => {
    void revision.value
    return service.listSnapshot(scope.value)
  })

  const requireScope = (): WorkspacePreferenceScope => {
    const current = scope.value
    if (!current) throw new TypeError('Account and workspace context are required')
    return current
  }

  return {
    entries: computed(() => listState.value.entries),
    listState,
    preference: <T = unknown>(key: string) =>
      computed(() => {
        void revision.value
        return service.snapshot<T>(scope.value, key)
      }),
    list: (options = {}) => service.list(requireScope(), options),
    get: <T = unknown>(key: string, options = {}) => service.get<T>(requireScope(), key, options),
    upsert: <T>(key: string, value: T) => service.upsert(requireScope(), key, value),
  }
}
